import * as THREE from 'three'
import type {
  GraphicsRequirements,
  WebGPUDeviceRequirements,
} from '@/graphics/GraphicsRequirements'
import { isGlobalGSplatClipVolume } from '@/plugins/gsplat/clipping/GSplatClipState'
import type { GSplatRelightingViewController } from '@/plugins/gsplat/effects/relighting/GSplatRelightingViewController'
import type { GSplatTrees } from '@/plugins/gsplat/effects/trees/GSplatTrees'
import type { GSplatTreesAttachment } from '@/plugins/gsplat/effects/trees/GSplatTreesGpuResources'
import type {
  GSplatWeatherController,
  GSplatWeatherOptions,
} from '@/plugins/gsplat/effects/weather/GSplatWeather'
import type {
  GSplatSystem,
  GSplatSystemRelightingViewControllerOptions,
} from '@/plugins/gsplat/GSplatSystem'
import {
  DefaultGaussianSplatSystemFactory,
  type GaussianSplatSystemFactory,
  type WebGPUGSplatSystemActivator,
} from '@/plugins/gsplat/GSplatSystemFactory'
import {
  createKHRGaussianSplattingHook,
  type GSplatGltfResourceFactory,
} from '@/plugins/gsplat/gltf/KHRGaussianSplatting'
import type { EngineContext } from '@/runtime/EngineContext'
import type { GltfExtensionRegistry } from '@/runtime/GltfExtensionRegistry'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type { ExportOptions, PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type {
  AssetId,
  ClipVolume,
  Configurable,
  Disposable,
  PickQuery,
  PickResult,
  RectPickQuery,
} from '@/shared/types/core'
import type { LODEventData } from '@/shared/types/gsplat'
import type { ExportableAssetPlugin } from '@/shared/types/plugin'
import type { RenderActivityContext, RenderActivityReport } from '@/shared/types/services'
import type { EngineFrameContext, ViewFrameContext, ViewId } from '@/shared/types/view'
import { generateId } from '@/shared/utils'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
  GaussianSplatPluginOptions,
  GaussianSplatRenderConfig,
  GSplatDeviceBudgetDiagnostics,
  GSplatResolvedSortingMode,
  LODConfig,
  LODStats,
} from './types/PluginTypes'
import { GSPLAT_PROJECTION_CACHE_BASE_WORDS } from './unified/GSplatProjectorConstants'

const log = getLoggerManager().getLogger('gsplat')
const DEFAULT_RENDER_CONFIG: GaussianSplatRenderConfig = Object.freeze({
  sortingMode: 'auto',
  qualityLevel: 'medium',
  maxSplats: 0,
  opacity: 1,
})

/** 格式不支持错误；保留 Plugin 公共错误形状，格式解析权威来源已经迁入 GSplatSystem。 */
export class FormatNotSupportedError extends Error {
  readonly url: string
  readonly formats: string[]

  constructor(url: string, formats: readonly string[]) {
    super(`无法加载 "${url}"：不支持该格式。已注册格式: ${formats.join(', ')}`)
    this.name = 'FormatNotSupportedError'
    this.url = url
    this.formats = [...formats]
  }
}

/** `backend` 仅是 PlayCanvas 实现谱系标签，不能再充当 graphics backend fallback。 */
export class GSplatImplementationLineageError extends Error {
  readonly code = 'GSPLAT_IMPLEMENTATION_LINEAGE_UNSUPPORTED'

  constructor(readonly lineage: string) {
    super(`GaussianSplatPlugin 不支持实现谱系 "${lineage}"；当前仅支持 "playcanvas"`)
    this.name = new.target.name
  }
}

/** 3DGS 公共插件 façade；内部唯一 runtime owner 为 GSplatSystem。 */
export class GaussianSplatPlugin
  implements
    ExportableAssetPlugin<GaussianSplatLoadRequest, GaussianSplatHandle>,
    Configurable<GaussianSplatRenderConfig>
{
  readonly name = 'gaussian-splat'
  readonly kind = 'gaussian-splat' as const
  readonly priority = 40
  readonly graphicsRequirements: GraphicsRequirements = Object.freeze({
    backends: Object.freeze(['webgl2', 'webgpu'] as const),
    capabilities: Object.freeze(['surfaceRender', 'multiView'] as const),
  })
  readonly webgpuDeviceRequirements: WebGPUDeviceRequirements
  readonly phases = [RenderPhase.Prepare, RenderPhase.MainTransparent]
  readonly renderPriority = 0
  readonly dependencies = ['CameraRig', 'RenderPipeline', 'PickingService']
  readonly provides = ['GaussianSplatService']

  /**
   * 旧终端会在每个 View render 前后临时改写全部 Handle 的 `visible`，以弥补旧版
   * GSplat renderer 不支持 camera layer mask 的限制。Unified renderer 已原生按 View
   * layer mask 构建独立 World；继续套用该兼容层会让共享 Placement 在同一帧反复
   * true/false，持续制造 World/sort 版本并导致多视口显隐串线。
   *
   * 保留终端既有 sentinel 名称，使未修改的旧宿主能识别当前服务无需再次包装。
   */
  readonly __pointcloudContrastLayerScopedRender: boolean

  private readonly options: Readonly<GaussianSplatPluginOptions>
  private readonly implementationLineage: 'playcanvas'
  private readonly systemFactory: GaussianSplatSystemFactory
  private readonly usesDefaultSystemFactory: boolean
  private readonly renderScene = new THREE.Scene()
  private readonly transientActivityReasons = new Set<string>()
  private readonly transientActivityByView = new Map<ViewId, Set<string>>()
  private pendingRenderConfig: Partial<GaussianSplatRenderConfig>
  private pendingLodConfig: Partial<LODConfig> | undefined
  private context: EngineContext | null = null
  private system: GSplatSystem | null = null
  private clipSubscription: Disposable | null = null
  private gltfExtensionRegistration: Disposable | null = null
  private webgpuActivator: WebGPUGSplatSystemActivator | null = null
  private started = false
  private disposed = false

  constructor(options: GaussianSplatPluginOptions = {}) {
    const requestedSortingMode =
      options.renderConfig?.sortingMode ?? DEFAULT_RENDER_CONFIG.sortingMode
    const configuredMaxSplats = options.renderConfig?.maxSplats ?? DEFAULT_RENDER_CONFIG.maxSplats
    const maxSplats =
      Number.isSafeInteger(configuredMaxSplats) && configuredMaxSplats >= 0
        ? configuredMaxSplats
        : DEFAULT_RENDER_CONFIG.maxSplats
    // 0 = 不限制：静态资产的真实占用由加载时的设备限制兜底（GSplatProjector 会按实际
    // allocationCount 校验 maxBufferSize / maxStorageBufferBindingSize），因此这里不声明预检门槛。
    const projectionCacheBytes =
      maxSplats * GSPLAT_PROJECTION_CACHE_BASE_WORDS * Uint32Array.BYTES_PER_ELEMENT
    const declaresProjectionCacheLimits = requestedSortingMode !== 'cpu' && maxSplats > 0
    this.webgpuDeviceRequirements = Object.freeze({
      features: Object.freeze([]),
      limits: Object.freeze(
        declaresProjectionCacheLimits
          ? [
              Object.freeze({ name: 'maxBufferSize' as const, minimum: projectionCacheBytes }),
              Object.freeze({
                name: 'maxStorageBufferBindingSize' as const,
                minimum: projectionCacheBytes,
              }),
            ]
          : []
      ),
    })
    const lineage = (options as { backend?: unknown }).backend ?? 'playcanvas'
    if (lineage !== 'playcanvas') {
      throw new GSplatImplementationLineageError(String(lineage))
    }
    this.implementationLineage = lineage
    this.options = Object.freeze({ ...options })
    this.pendingRenderConfig = { ...(options.renderConfig ?? {}) }
    this.pendingLodConfig = options.lodConfig ? { ...options.lodConfig } : undefined
    this.usesDefaultSystemFactory = options.systemFactory === undefined
    this.__pointcloudContrastLayerScopedRender = this.usesDefaultSystemFactory
    this.systemFactory =
      options.systemFactory ?? new DefaultGaussianSplatSystemFactory(() => this.webgpuActivator)
  }

  async preload(context: EngineContext): Promise<void> {
    if (!this.usesDefaultSystemFactory || context.graphics.kind !== 'webgpu') return
    const module = await import('@/plugins/gsplat/system/WebGPUGSplatUnifiedComposition')
    this.webgpuActivator = module.activateWebGPUGSplatSystem
  }

  rollbackPreload(): void {
    this.webgpuActivator = null
  }

  onInit(context: EngineContext): void {
    if (this.context || this.system) throw new Error('GaussianSplatPlugin 已初始化')
    this.context = context
    try {
      this.system = this.systemFactory.create({
        context,
        renderScene: this.renderScene,
        implementationLineage: this.implementationLineage,
        options: Object.freeze({ ...this.options, lodConfig: this.pendingLodConfig }),
        initialConfig: this.pendingRenderConfig,
        onVisualActivity: (reason, id, viewId) => this.markVisualActivity(reason, id, viewId),
        onLodError: (data) => this.handleLodError(data),
      })
      if (Object.keys(this.pendingRenderConfig).length > 0) {
        this.system.setConfig(this.pendingRenderConfig)
        this.pendingRenderConfig = {}
      }
      this.registerGltfHook(context, this.options.gltfResourceFactory)
      context.registerService('GaussianSplatService', this)
      log.debug(
        `[GaussianSplatPlugin] initialized, lineage=${this.implementationLineage}, graphics=${context.graphics.kind}`
      )
    } catch (error) {
      this.gltfExtensionRegistration?.dispose()
      this.gltfExtensionRegistration = null
      this.system?.dispose()
      this.system = null
      this.context = null
      throw error
    }
  }

  onStart(): void {
    const context = this.requireContext()
    context.getService('RenderPipeline').addRenderContributor(this)
    context.getService('PickingService').addProvider(this)
    this.started = true

    if (context.hasService('ClipService')) {
      const clipService = context.getService('ClipService')
      this.applyClipVolumes(clipService.volumes ?? [])
      this.clipSubscription = clipService.onChanged((volumes) => this.applyClipVolumes(volumes))
    }
  }

  onUpdateFrame(frame: EngineFrameContext): void {
    this.system?.updateFrame(frame)
  }

  onDestroy(): void {
    if (this.disposed) return
    const context = this.context
    this.clipSubscription?.dispose()
    this.clipSubscription = null
    this.gltfExtensionRegistration?.dispose()
    this.gltfExtensionRegistration = null

    if (context && this.started) {
      context.getService('RenderPipeline').removeRenderContributor(this)
      context.getService('PickingService').removeProvider(this)
    }
    this.started = false

    const system = this.system
    if (system) {
      for (const handle of system.getAll()) {
        context?.sceneBounds.unregister(this.kind, handle.id)
      }
      system.dispose()
    }
    this.system = null
    this.context = null
    this.transientActivityReasons.clear()
    this.transientActivityByView.clear()
    this.webgpuActivator = null
    this.disposed = true
    log.debug('[GaussianSplatPlugin] destroyed')
  }

  async load(request: GaussianSplatLoadRequest): Promise<GaussianSplatHandle> {
    const context = this.requireContext()
    const system = this.requireSystem()
    const id = request.id ?? generateId()
    const normalized = Object.freeze({ ...request, id })
    const startedAt = getLoggerManager().now()
    let lastProgress = -1
    const emitProgress = (value: number) => {
      const progress = Math.min(Math.max(Number.isFinite(value) ? value : 0, 0), 1)
      if (progress === lastProgress) return
      lastProgress = progress
      context.events.emit('asset.progress', {
        id,
        kind: this.kind,
        progress,
        loaded: progress,
        total: 1,
      })
    }
    context.events.emit('asset.loading', { id, kind: this.kind })
    try {
      const handle = await system.load(normalized, {
        onProgress: emitProgress,
      })
      emitProgress(1)
      context.events.emit('asset.loaded', { id, kind: this.kind })
      this.markVisualActivity('gsplat.assetLoaded', id)
      log.debug(
        `[GaussianSplatPlugin] load completed, id=${id}, splats=${handle.numSplats}, elapsed=${(getLoggerManager().now() - startedAt).toFixed(1)}ms`
      )
      return handle
    } catch (error) {
      const mapped = this.mapLoadError(normalized, error)
      if (!(mapped instanceof DOMException && mapped.name === 'AbortError')) {
        context.events.emit('asset.error', {
          id,
          kind: this.kind,
          error: mapped instanceof Error ? mapped : new Error(String(mapped)),
        })
      }
      throw mapped
    }
  }

  mount(handle: GaussianSplatHandle): void {
    const context = this.requireContext()
    this.requireSystem().mount(handle)
    if (handle.object3D) {
      context.sceneBounds.register({
        id: handle.id,
        kind: this.kind,
        object3D: handle.object3D,
        getLocalBoundingBox: () => handle.boundingBox,
        isVisible: () => handle.visible,
      })
    }
    context.events.emit('asset.mounted', { id: handle.id, kind: this.kind })
    this.markVisualActivity('gsplat.mounted', handle.id)
  }

  unmount(id: AssetId): void {
    const system = this.requireSystem()
    const handle = system.getAsset(id)
    if (!handle) {
      log.warn(`[GaussianSplatPlugin] unmount 跳过：未找到资产 "${id}"`)
      return
    }
    system.unmount(id)
    const context = this.requireContext()
    context.sceneBounds.unregister(this.kind, id)
    context.events.emit('asset.unmounted', { id, kind: this.kind })
    this.markVisualActivity('gsplat.unmounted', id)
  }

  getAsset(id: AssetId): GaussianSplatHandle | undefined {
    return this.system?.getAsset(id)
  }

  getAll(): GaussianSplatHandle[] {
    return this.system?.getAll() ?? []
  }

  getByUrl(url: string): GaussianSplatHandle | undefined {
    return this.system?.getByUrl(url)
  }

  isLoading(url: string): boolean {
    return this.system?.isLoading(url) ?? false
  }

  setVisibility(id: AssetId, visible: boolean): void {
    const system = this.system
    if (!system?.getAsset(id)) return
    system.setVisibility(id, visible)
    this.requireContext().events.emit('asset.visibilityChanged', { id, visible })
    this.markVisualActivity('gsplat.visibility', id)
  }

  getRenderActivity(context: RenderActivityContext): RenderActivityReport {
    if (context.renderedViewIds.length === 0) {
      this.transientActivityReasons.clear()
      this.transientActivityByView.clear()
      return { needsNextFrame: false, reasons: [] }
    }
    const reasons = [...this.transientActivityReasons]
    this.transientActivityReasons.clear()
    const viewIds = new Set<ViewId>()
    for (const [viewId, viewReasons] of this.transientActivityByView) {
      if (context.renderedViewIds.includes(viewId)) {
        this.transientActivityByView.delete(viewId)
      } else {
        reasons.push(...viewReasons)
        viewIds.add(viewId)
      }
    }
    const systemActivity = this.system?.getVisualActivitySnapshot()
    let hasGlobalActivity = false
    if (systemActivity?.needsNextFrame) {
      reasons.push(...systemActivity.reasons)
      if (systemActivity.viewIds?.length) {
        for (const viewId of systemActivity.viewIds) viewIds.add(viewId)
      } else {
        hasGlobalActivity = true
      }
    }
    return {
      needsNextFrame: reasons.length > 0,
      reasons: [...new Set(reasons)],
      ...(!hasGlobalActivity && viewIds.size > 0 ? { viewIds: [...viewIds] } : {}),
    }
  }

  render(frame: ViewFrameContext, phase: RenderPhase, context: PhaseRenderContext): void {
    const system = this.system
    if (!system) return
    if (phase === RenderPhase.Prepare) {
      system.prepareView(frame)
      return
    }
    if (phase !== RenderPhase.MainTransparent) return
    system.renderView(frame, context)
  }

  pick(query: PickQuery): PickResult | null {
    return this.system?.pick(query) ?? null
  }

  /** 异步点拾取优先进入 System-owned GPU coordinator；CPU baseline 在边界内兼容回退。 */
  async pickAsync(query: PickQuery): Promise<PickResult | null> {
    const system = this.system
    if (!system) return null
    try {
      return await system.pickGpu(query)
    } catch (error) {
      // WebGPU 异步入口一旦进入私有 ID pass，普通查询也不得再伪造 CPU 命中；
      // WebGL 仍保留旧 GPU→CPU 兼容回退。
      if (query.gpuOnly || query.signal?.aborted || this.context?.graphics.kind === 'webgpu') {
        throw error
      }
      return system.pick(query)
    }
  }

  pickGpu(query: PickQuery): Promise<PickResult | null> {
    return this.requireSystem().pickGpu(query)
  }

  pickRect(query: RectPickQuery): PickResult[] {
    return this.system?.pickRect(query) ?? []
  }

  async pickRectAsync(query: RectPickQuery): Promise<PickResult[]> {
    const system = this.system
    if (!system) return []
    try {
      return await system.pickRectGpu(query)
    } catch (error) {
      if (query.gpuOnly || query.signal?.aborted || this.context?.graphics.kind === 'webgpu') {
        throw error
      }
      return system.pickRect(query)
    }
  }

  dispose(id?: AssetId): void {
    const system = this.system
    const context = this.context
    if (!system || !context) return
    const ids = id === undefined ? system.getAll().map((handle) => handle.id) : [id]
    for (const assetId of ids) {
      const existed = system.disposeAsset(assetId)
      if (!existed) continue
      context.sceneBounds.unregister(this.kind, assetId)
      context.events.emit('asset.disposed', { id: assetId, kind: this.kind })
    }
  }

  async export(id: AssetId, format: string, options?: ExportOptions): Promise<Blob> {
    const context = this.requireContext()
    try {
      context.events.emit('asset.exportStarted', { id, kind: this.kind, format })
      const result = await this.requireSystem().export(id, format, options)
      context.events.emit('asset.exportCompleted', { id, kind: this.kind, format })
      return result
    } catch (error) {
      const mapped =
        error instanceof Error && error.message.includes('不支持导出格式')
          ? new FormatNotSupportedError(`(export:${id})`, [
              'ply',
              'splat',
              'compressed-ply',
              ...(this.options.extraExporters ?? []).map(({ format: name }) => name),
            ])
          : error
      context.events.emit('asset.exportError', {
        id,
        kind: this.kind,
        format,
        error: mapped instanceof Error ? mapped : new Error(String(mapped)),
      })
      throw mapped
    }
  }

  getConfig(): GaussianSplatRenderConfig {
    return this.system?.getConfig() ?? { ...DEFAULT_RENDER_CONFIG, ...this.pendingRenderConfig }
  }

  /** 当前实际运行的排序管线；初始化前按当前 graphics（未知时按 WebGL 兼容默认）解析。 */
  get resolvedSortingMode(): GSplatResolvedSortingMode {
    if (this.system) return this.system.resolvedSortingMode
    const requested = this.getConfig().sortingMode
    return this.context?.graphics.kind === 'webgpu' && requested !== 'cpu' ? 'gpu' : 'cpu'
  }

  setConfig(config: Partial<GaussianSplatRenderConfig>): void {
    if (this.system) this.system.setConfig(config)
    else this.pendingRenderConfig = { ...this.pendingRenderConfig, ...config }
    this.markVisualActivity('gsplat.config')
    if (log.isEnabled(LogLevel.DEBUG)) {
      log.debug(
        `[GaussianSplatPlugin] render config updated, keys=${Object.keys(config).join(',')}`
      )
    }
  }

  /** 提前编译裁切 shader 变体，避免右键完成框选时才触发首次 pipeline 编译。 */
  prewarmClip(maxVertices: number, maxVolumes = 1): boolean {
    if (!this.system?.prewarmClip(maxVertices, maxVolumes)) return false
    this.markVisualActivity('gsplat.clipPrewarm')
    return true
  }

  releaseClipPrewarm(): boolean {
    const changed = this.system?.releaseClipPrewarm() ?? false
    if (changed) this.markVisualActivity('gsplat.clipPrewarmRelease')
    return changed
  }

  setLodConfig(config: Partial<LODConfig>): void {
    this.pendingLodConfig = { ...(this.pendingLodConfig ?? {}), ...config }
    this.system?.setLodConfig(config)
    this.markVisualActivity('gsplat.lodConfig')
  }

  getLodConfig(): LODConfig {
    return this.requireSystem().getLodConfig()
  }

  getLodStats(): LODStats {
    return this.requireSystem().getLodStats()
  }

  getDeviceBudgetDiagnostics(): GSplatDeviceBudgetDiagnostics {
    return this.requireSystem().getDeviceBudgetDiagnostics()
  }

  /** 将 Trees effect 绑定到指定 Unified asset，并由 System 管理资源/owner 生命周期。 */
  attachTrees(id: AssetId, trees: GSplatTrees): GSplatTreesAttachment {
    return this.requireSystem().attachTrees(id, trees)
  }

  /** 创建 System-owned 的 per-View Relighting controller。 */
  createRelightingViewController(
    options: GSplatSystemRelightingViewControllerOptions
  ): GSplatRelightingViewController {
    return this.requireSystem().createRelightingViewController(options)
  }

  /** 释放一个由当前插件创建的 Relighting controller。 */
  disposeRelightingViewController(controller: GSplatRelightingViewController): boolean {
    return this.requireSystem().disposeRelightingViewController(controller)
  }

  /** 创建并挂载 System-owned 的程序化 Weather generator。 */
  createWeather(options: GSplatWeatherOptions = {}): GSplatWeatherController {
    return this.requireSystem().createWeather(options)
  }

  private registerGltfHook(
    context: EngineContext,
    resourceFactory?: GSplatGltfResourceFactory
  ): void {
    if (!resourceFactory) return
    const registry: GltfExtensionRegistry =
      this.options.gltfExtensionRegistry ?? context.gltfExtensions
    this.gltfExtensionRegistration = registry.register(
      createKHRGaussianSplattingHook(resourceFactory)
    )
  }

  private applyClipVolumes(volumes: ReadonlyArray<ClipVolume>): void {
    const changed = this.requireSystem().setClipVolumes(volumes.filter(isGlobalGSplatClipVolume))
    if (!changed) return
    this.markVisualActivity('gsplat.clipVolumes')
  }

  private handleLodError(data: LODEventData['loadError']): void {
    const node =
      typeof data.node === 'object' && data.node !== null
        ? (data.node as { nodeId?: unknown; url?: unknown })
        : {}
    if (typeof node.nodeId !== 'string' || node.nodeId.length === 0) {
      this.context?.events.emit('engine.error', { error: data.error, context: 'gsplat.lod' })
      return
    }
    const resourceUrl = typeof node.url === 'string' ? node.url : ''
    log.warn(
      resourceUrl
        ? `[GaussianSplatPlugin] LOD 资源已耗尽重试，将跳过并继续加载：${resourceUrl} (${data.error.message})`
        : `[GaussianSplatPlugin] LOD 资源已耗尽重试，将跳过并继续加载：${data.error.message}`
    )
  }

  private mapLoadError(request: GaussianSplatLoadRequest, error: unknown): unknown {
    if (
      error instanceof Error &&
      error.message.startsWith(`GSplatSystem: 不支持 "${request.url}"`)
    ) {
      return new FormatNotSupportedError(request.url, this.requireSystem().loaderFormats)
    }
    return error
  }

  private markVisualActivity(reason: string, id?: AssetId, viewId?: ViewId): void {
    if (viewId) {
      const reasons = this.transientActivityByView.get(viewId) ?? new Set<string>()
      reasons.add(reason)
      this.transientActivityByView.set(viewId, reasons)
      this.context?.renderInvalidation.invalidate(reason, {
        viewId,
        ...(id ? { detail: id } : {}),
      })
      return
    }
    this.transientActivityReasons.add(reason)
    this.context?.renderInvalidation.invalidate(reason, id ? { detail: id } : undefined)
  }

  private requireContext(): EngineContext {
    if (!this.context) throw new Error('GaussianSplatPlugin 尚未初始化或已销毁')
    return this.context
  }

  private requireSystem(): GSplatSystem {
    if (!this.system) throw new Error('GaussianSplatPlugin 尚未初始化或已销毁')
    return this.system
  }
}
